<script setup lang="ts">
import { computed, useSlots } from 'vue';

import { AppSvgIcon, AppTooltip } from '@libs/ui';

import { PriceMaskType, TileSelectionType } from '../models';

interface CalendarTileLayoutProps {
  priceType: PriceMaskType;
  selectionType: TileSelectionType;
  allowSelection: boolean;
  tooltipText?: string;
}

const props = defineProps<CalendarTileLayoutProps>();

interface CalendarTileLayoutEmits {
  (eventName: 'click'): void;
}

const emit = defineEmits<CalendarTileLayoutEmits>();

const slots = useSlots();
const PRICE_SLOT_NAME = 'price';

const hasPrice = computed(() => !!slots[PRICE_SLOT_NAME]);

const tooltipTriggerColor = computed(() =>
  props.selectionType === TileSelectionType.Selected
    ? 'color-white'
    : 'color-neutral-70'
);

function onClick(): void {
  if (props.allowSelection) {
    emit('click');
  }
}
</script>

<template>
  <button
    class="calendar-tile-layout"
    :class="{
      cheap: props.priceType === PriceMaskType.Cheap,
      moderate: props.priceType === PriceMaskType.Moderate,
      expensive: props.priceType === PriceMaskType.Expensive,
      selected: props.selectionType === TileSelectionType.Selected,
      suggested: props.selectionType === TileSelectionType.Suggested,
      'allow-hover': props.allowSelection,
    }"
    @click="onClick"
  >
    <div class="day">
      <div class="day-text">
        <slot name="day" />
      </div>
    </div>
    <div v-if="hasPrice" class="price-text">
      <slot :name="PRICE_SLOT_NAME" />
    </div>

    <AppTooltip v-if="tooltipText" position="top-left" class="info-tooltip">
      <template #trigger>
        <AppSvgIcon name="infoCircle" size="16" :color="tooltipTriggerColor" />
      </template>
      <template #default>{{ props.tooltipText }}</template>
    </AppTooltip>
  </button>
</template>

<style scoped lang="scss">
@import '../styles/price_calendar_mixins';

.calendar-tile-layout {
  width: var(--calendar-tile-size);
  height: var(--calendar-tile-size);
  border-radius: var(--calendar-tile-border-radius);
  background-color: var(--card-tile-background-color);
  transition: background-color var(--transition-short);
  cursor: default;

  display: flex;
  gap: var(--calendar-tile-day-price-gap);
  flex-direction: column;
  align-items: center;
  justify-content: center;

  position: relative;

  &.allow-hover {
    @include on-hover {
      background-color: var(--calendar-tile-hover-color);
      cursor: pointer;
    }
  }

  &.cheap {
    .price-text {
      color: var(--color-success-dark);
    }
  }

  &.selected {
    background-color: var(--color-neutral-70);

    &.allow-hover {
      @include on-hover {
        background-color: var(--color-neutral-70);
        transition: background-color var(--transition-short);
        cursor: pointer;
      }
    }

    .day {
      .day-text {
        color: var(--color-white);
      }
    }

    .price-text {
      color: var(--color-white);
    }
  }

  .day {
    position: relative;

    .day-text {
      position: relative;
      z-index: 2;
      color: var(--calendar-tile-day-font-color);
      @include calendar-tile-day-font;
    }
  }

  .price-text {
    @include calendar-tile-price-font;
    color: var(--calendar-tile-price-font-color);
  }

  .info-tooltip {
    position: absolute;
    padding: 0;
    right: var(--calendar-tile-info-icon-right);
    top: var(--calendar-tile-info-icon-top);
    color: var(--calendar-tile-day-font-color);
    @include for-less-or-equal-than-599-px {
      right: var(--calendar-tile-info-icon-right-mobile);
    }
  }
}
</style>
